import React, { useState } from "react";
import PropTypes from 'prop-types';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  TextField,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Badge,
  useTheme,
  useMediaQuery,
  InputAdornment,
  ListItemIcon,
  Divider,
} from "@mui/material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import SearchIcon from "@mui/icons-material/Search";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SettingsIcon from "@mui/icons-material/Settings";
import LanguageIcon from "@mui/icons-material/Language";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";

// Import LogoutButton from molecules
import LogoutButton from "../../molecules/LogoutButton/LogoutButton";
import Notification from "../../Dashboard/Notification";


const Header = ({
  logo,
  appName = "",
  user,
  onThemeToggle,
  onSearch,
  onProfileMenuClick,
  onNotificationsClick,
  notificationCount = 0,
  searchPlaceholder = "Search...",
  theme: currentTheme = "light"
}) => {
  const [anchorNotif, setAnchorNotif] = useState(null);
  const [anchorProfile, setAnchorProfile] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));


  const handleNotifMenu = (event) => {
    setAnchorNotif(event.currentTarget);
    if (onNotificationsClick) {
      onNotificationsClick();
    }
  };

  const handleProfileMenu = (event) => {
    setAnchorProfile(event.currentTarget);
    if (onProfileMenuClick) {
      onProfileMenuClick();
    }
  };

  const handleClose = () => {
    setAnchorNotif(null);
    setAnchorProfile(null);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleThemeToggle = () => {
    if (onThemeToggle) {
      onThemeToggle(currentTheme === "light" ? "dark" : "light");
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        height: 64,
        backgroundColor: "primary.dark",
        backgroundImage: "none",
        zIndex: theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar
        sx={{
          minHeight: 64,
          display: "flex",
          justifyContent: "space-between",
          px: { xs: 1.5, sm: 2 },
          gap: 2
        }}
      >
        {/* Logo and Brand */}
        <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0, flexShrink: 1 }}>
          {logo && (
            <img
              src={logo}
              alt="Logo"
              style={{
                // width: 36,
                height: 36,
                objectFit: "cover",
              }}
            />
          )}
          {/* <Typography
            variant="h6"
            noWrap
            sx={{
              fontWeight: 600,
              display: { xs: "none", sm: "block" }
            }}
          >
            {appName}
          </Typography> */}
        </Box>

        {/* Search - Hidden on mobile if no space */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            flex: 1,
            maxWidth: 500,
            mx: 2
          }}
        >
          <TextField
            fullWidth
            size="small"
            hiddenLabel
            variant="filled"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{
              backgroundColor: "primary.contrastText",
              borderRadius: 1,
              "& .MuiInputBase-input": {
                color: "primary.dark",
              },
              "& .MuiFilledInput-root": {
                borderRadius: 1,
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Actions */}
        <Box display="flex" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
          {/* Theme toggle */}
          <IconButton
            color="inherit"
            size="medium"
            onClick={handleThemeToggle}
            sx={{ p: 1 }}
          >
            {currentTheme === "light" ? <DarkModeIcon /> : <LightModeIcon />}
          </IconButton>

          {/* Notifications */}
          <IconButton
            color="inherit"
            size="medium"
            onClick={handleNotifMenu}
            sx={{ p: 1 }}
          >
            <Badge
              badgeContent={notificationCount}
              color="error"
              variant={notificationCount > 0 ? "standard" : "dot"}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <Menu
            anchorEl={anchorNotif}
            open={Boolean(anchorNotif)}
            onClose={handleClose}
            PaperProps={{
              sx: {
                // width: 320,
                mt: 1,
                ml:-2
              }
            }}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          >
            <MenuItem onClick={handleClose}>
              <Notification />
            </MenuItem>
          </Menu>

          {/* Profile */}
          <IconButton
            size="medium"

            onClick={handleProfileMenu}
            sx={{
              p: 0.8,
              ml: 0.5,
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                backgroundColor: "primary.contrastText"
              }}
              src={user?.photoURL}
            >
              <AccountCircle fontSize="small" color="primary" />
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorProfile}
            open={Boolean(anchorProfile)}
            onClose={handleClose}
            PaperProps={{
              sx: {
                width: 220,
                mt: 1,
                borderRadius: 2,
              }
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            {user && (
              <MenuItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1.5 }}>
                <Typography variant="subtitle2" noWrap>
                  {user.displayName || user.user_name || 'User'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {user.email || user.user_email}
                </Typography>
              </MenuItem>
            )}

            <Divider />

            <MenuItem onClick={handleClose} sx={{ py: 1 }}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" sx={{ color: "primary.main" }} />
              </ListItemIcon>
              <Typography variant="body2">Settings</Typography>
            </MenuItem>

            <MenuItem onClick={handleClose} sx={{ py: 1 }}>
              <ListItemIcon>
                <LanguageIcon fontSize="small" sx={{ color: "primary.main" }} />
              </ListItemIcon>
              <Typography variant="body2">Language</Typography>
            </MenuItem>

            <MenuItem onClick={handleThemeToggle} sx={{ py: 1 }}>
              <ListItemIcon>
                {currentTheme === "light" ? (
                  <DarkModeIcon fontSize="small" sx={{ color: "primary.main" }} />
                ) : (
                  <LightModeIcon fontSize="small" sx={{ color: "primary.main" }} />
                )}
              </ListItemIcon>
              <Typography variant="body2">
                {currentTheme === "light" ? "Dark Mode" : "Light Mode"}
              </Typography>
            </MenuItem>

            <Divider />

            {/* Using LogoutButton from molecules */}
            <MenuItem sx={{ justifyContent: "center", py: 1.5 }}>
              <LogoutButton
                variant="outlined"
                color="error"
                size="small"
                showConfirmation={true}
                onLogoutStart={handleClose}
              />
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

Header.propTypes = {
  logo: PropTypes.string,
  appName: PropTypes.string,
  user: PropTypes.object,
  onThemeToggle: PropTypes.func,
  onSearch: PropTypes.func,
  onProfileMenuClick: PropTypes.func,
  onNotificationsClick: PropTypes.func,
  notificationCount: PropTypes.number,
  searchPlaceholder: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark'])
};

export default Header;